/**
 * useDailyCall — Browser WebRTC hook for Daily.co video calls
 * Wraps @daily-co/daily-js DailyCall instance for practice-web.
 */
import { useState, useCallback, useRef } from 'react';
import type { DailyCall, DailyParticipant } from '@daily-co/daily-js';
import type { CallState, ConnectionQuality } from '@/types/video-consultation';
import { logger } from '@/lib/logger';

interface DailyParticipantInfo {
  user_id: string;
  user_name: string;
  audio: boolean;
  video: boolean;
  screen: boolean;
  local: boolean;
}

export interface UseDailyCallReturn {
  callState: CallState;
  lastError: string | null;
  localParticipant: DailyParticipantInfo | null;
  remoteParticipants: DailyParticipantInfo[];
  isMicOn: boolean;
  isCameraOn: boolean;
  isScreenSharing: boolean;
  blurOn: boolean;
  connectionQuality: ConnectionQuality;
  callDurationSeconds: number;
  joinCall: (roomUrl: string, token: string, audioDeviceId?: string | false, videoDeviceId?: string | false) => Promise<void>;
  leaveCall: () => Promise<void>;
  toggleMic: () => void;
  toggleCamera: () => void;
  toggleBlur: () => Promise<void>;
  startScreenShare: () => Promise<void>;
  stopScreenShare: () => void;
  /** Raw DailyCall object — reactive state, updates when call connects/disconnects */
  callFrame: unknown;
}

function mapParticipant(p: DailyParticipant, isLocal: boolean): DailyParticipantInfo {
  return {
    user_id: p.user_id ?? '',
    user_name: p.user_name ?? '',
    audio: !!p.audio,
    video: !!p.video,
    screen: !!p.screen,
    local: isLocal,
  };
}

function getDailyErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    const nested = [record.errorMsg, record.message, record.error, record.details, record.detail];
    for (const candidate of nested) {
      const message = getDailyErrorMessage(candidate);
      if (message && message !== '[object Object]') {
        return message;
      }
    }
  }

  return 'Daily-Verbindung fehlgeschlagen';
}

function describeInputSource(source?: string | false): 'disabled' | 'default' | 'selected-device' {
  if (source === false) {
    return 'disabled';
  }

  if (typeof source === 'string') {
    return 'selected-device';
  }

  return 'default';
}

export function useDailyCall(): UseDailyCallReturn {
  const callRef = useRef<DailyCall | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const startTimeRef = useRef<number>(0);

  const [callState, setCallState] = useState<CallState>('idle');
  const [lastError, setLastError] = useState<string | null>(null);
  const [callFrame, setCallFrame] = useState<DailyCall | null>(null);
  const [localParticipant, setLocalParticipant] = useState<DailyParticipantInfo | null>(null);
  const [remoteParticipants, setRemoteParticipants] = useState<DailyParticipantInfo[]>([]);
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [blurOn, setBlurOn] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>('unknown');
  const [callDurationSeconds, setCallDurationSeconds] = useState(0);

  const destroyCurrentCall = useCallback(async () => {
    clearInterval(timerRef.current);

    if (callRef.current) {
      const existingCall = callRef.current;
      callRef.current = null;

      await existingCall.leave().catch(() => undefined);
      await existingCall.destroy().catch(() => undefined);
    }

    setCallFrame(null);
    setRemoteParticipants([]);
    setLocalParticipant(null);
    setIsScreenSharing(false);
    setConnectionQuality('unknown');
  }, []);

  const updateParticipants = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const participants = call.participants();
    if (participants.local) {
      setLocalParticipant(mapParticipant(participants.local, true));
      setIsMicOn(!!participants.local.audio);
      setIsCameraOn(!!participants.local.video);
      setIsScreenSharing(!!participants.local.screen);
    }
    const remote = Object.values(participants).filter(
      (p): p is DailyParticipant => !p.local
    );
    setRemoteParticipants(remote.map((p) => mapParticipant(p, false)));
  }, []);

  const joinCall = useCallback(async (roomUrl: string, token: string, audioDeviceId?: string | false, videoDeviceId?: string | false) => {
    setCallState('connecting');
    setLastError(null);

    const joinMode = {
      audio: describeInputSource(audioDeviceId),
      video: describeInputSource(videoDeviceId),
    };

    logger.info('Daily join requested', {
      joinMode,
      hasToken: Boolean(token),
      roomHost: (() => {
        try {
          return new URL(roomUrl).host;
        } catch {
          return 'invalid-room-url';
        }
      })(),
    });

    try {
      await destroyCurrentCall();

      const DailyModule = await import('@daily-co/daily-js');
      const Daily = DailyModule.default;
      const call = Daily.createCallObject({
        audioSource: audioDeviceId === false ? false : audioDeviceId ?? true,
        videoSource: videoDeviceId === false ? false : videoDeviceId ?? true,
      });
      callRef.current = call;
      setCallFrame(call);

      call.on('joined-meeting', () => {
        logger.info('Daily joined meeting', {
          joinMode,
        });
        setCallState('connected');
        startTimeRef.current = Date.now();
        timerRef.current = setInterval(() => {
          setCallDurationSeconds(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 1000);
        updateParticipants();
      });

      call.on('left-meeting', () => {
        logger.info('Daily left meeting', {
          joinMode,
        });
        setCallState('disconnected');
        clearInterval(timerRef.current);
        setLastError(null);
      });

      call.on('participant-joined', () => updateParticipants());
      call.on('participant-updated', () => updateParticipants());
      call.on('participant-left', () => updateParticipants());

      call.on('error', (event: unknown) => {
        const errorMessage = getDailyErrorMessage(event);
        logger.error('Daily call error', {
          error: errorMessage,
          joinMode,
        });
        setLastError(errorMessage);
        setCallState('error');
        clearInterval(timerRef.current);
      });

      call.on('network-quality-change', (event) => {
        const state = event?.networkState;
        if (state === 'good') setConnectionQuality('good');
        else if (state === 'warning') setConnectionQuality('fair');
        else if (state === 'bad') setConnectionQuality('poor');
        else setConnectionQuality('unknown');
      });

      await call.join({
        url: roomUrl,
        token,
      });
    } catch (error) {
      const message = getDailyErrorMessage(error);
      logger.error('Daily join failed', {
        error: message,
        joinMode,
      });
      await destroyCurrentCall();
      setLastError(message);
      setCallState('error');
    }
  }, [destroyCurrentCall, updateParticipants]);

  const leaveCall = useCallback(async () => {
    setCallState('disconnecting');
    await destroyCurrentCall();
    setCallState('disconnected');
    setLastError(null);
  }, [destroyCurrentCall]);

  const toggleMic = useCallback(() => {
    if (!callRef.current) return;
    callRef.current.setLocalAudio(!isMicOn);
    setIsMicOn(!isMicOn);
  }, [isMicOn]);

  const toggleCamera = useCallback(() => {
    if (!callRef.current) return;
    callRef.current.setLocalVideo(!isCameraOn);
    setIsCameraOn(!isCameraOn);
  }, [isCameraOn]);

  const startScreenShare = useCallback(async () => {
    if (!callRef.current) return;
    await callRef.current.startScreenShare();
    setIsScreenSharing(true);
  }, []);

  const stopScreenShare = useCallback(() => {
    if (!callRef.current) return;
    callRef.current.stopScreenShare();
    setIsScreenSharing(false);
  }, []);

  const toggleBlur = useCallback(async () => {
    const call = callRef.current;
    if (!call) return;
    try {
      const newBlur = !blurOn;
      await call.updateInputSettings({
        video: {
          processor: newBlur
            ? { type: 'background-blur' as const, config: { strength: 0.5 } }
            : { type: 'none' as const },
        },
      });
      setBlurOn(newBlur);
    } catch {
      // Blur not supported — silently ignore
    }
  }, [blurOn]);

  return {
    callState,
    lastError,
    localParticipant,
    remoteParticipants,
    isMicOn,
    isCameraOn,
    isScreenSharing,
    blurOn,
    connectionQuality,
    callDurationSeconds,
    joinCall,
    leaveCall,
    toggleMic,
    toggleCamera,
    toggleBlur,
    startScreenShare,
    stopScreenShare,
    callFrame,
  };
}
